import { GameNews } from "@dragonsight/models";
import { Api } from "../../shared/api";
import { useParams } from "react-router-dom";
import { Stack, styled, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Formik } from "formik";
import { TextInput } from "../../components/form";
import ContentContainer from "../../components/contentContainer";
import Section from "../../components/section";
import Button from "../../components/button";

import { AddAlert } from "../../shared/alert";
import { useConfirmModal } from "../../components/confirmModal";
import CopyBox from "../../components/copybox";
import { token } from "../../shared/theme";

const NewsName = styled('div')({
    fontWeight: 700,
});

const NewsContent = styled('div')({
    whiteSpace: "pre-line"
})

const publicApi = process.env.REACT_APP_PUBLIC_API_ENDPOINT;

type FormGameNews = Omit<GameNews, "id" | "gameId" | "createdAt" | "updatedAt">

const GameNewsRow = (props: { news: GameNews, onChange: (item: { name: string, content: string}) => void, onDelete: () => void}) => {
    const { news, onChange, onDelete } = props;
    const [ content, setContent ] = useState(news.content);
    const [ name, setName ] = useState(news.name);
    const [ editing, setEditing ] = useState(false);
    const confirm = useConfirmModal();

    const submit = (values: FormGameNews) => {
        setEditing(false);
        onChange(values);
    }

    const handleDelete = async () => {
        const willDelete = await confirm.confirm({
            message: "Are you sure you want to delete this game news entry.",
            title: `Delete ${news.name}`
        });

        if(willDelete) {
            onDelete();
        }
    }

    return <div key={news.id}>
        {editing ? <>
            <Formik<FormGameNews> onSubmit={submit} initialValues={{name: name, content: content}}>
                {({submitForm, resetForm}) => (
                    <>
                        <Stack spacing={2}>
                            <TextInput name="name" placeholder="News Subject" />
                            <TextInput name="content" placeholder="Your update goes here" multiline />
                            <Stack direction={"row"} spacing={1}>
                                <Button variant="contained" onClick={() => { resetForm(); setEditing(false); }}>Cancel Changes</Button>
                                <Button variant="contained" onClick={() => submitForm()}>Confirm Changes</Button>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Formik>
        </> : 
        <>
            <NewsName>{news.name}</NewsName>
            <NewsContent>{news.content}</NewsContent>
            <Stack direction={"row"} spacing={1} mt={2}>
                <Button variant="contained" onClick={() => setEditing(true)}>Edit</Button>
                <Button variant="contained" onClick={() => handleDelete()}>Delete</Button>
            </Stack>
        </>}
    </div>
}

const NewItem = (props: { onSubmit: (values: FormGameNews) => void}) => {
    return <Formik<FormGameNews> onSubmit={props.onSubmit} initialValues={{name: "", content: ""}}>
        {({values, handleChange, submitForm}) => (
            <>
                <Stack spacing={2}>
                    <TextInput name="name" placeholder="News Subject" />
                    <TextInput name="content" placeholder="Your update goes here" multiline />
                    <Stack direction={"row"}>
                        <Button variant="contained" onClick={() => submitForm()}>Add news</Button>
                    </Stack>
                </Stack>
            </>
        )}
    </Formik>
}


const GameNewsScreen = () => {
    const { gameId } = useParams();
    const [ news, setNews ] = useState<GameNews[]>([]);
    const [ loading, setLoading ] = useState(false);

    const list = async (search: "", nextToken?: string) => {
        setLoading(true);
        try {
            const response = await Api.Game.News.List(gameId!, search, nextToken, undefined);
            setNews(response.items);
        } finally {
            setLoading(false);
        }

    }

    const addConfig = async (values: FormGameNews) => {
        const response = await Api.Game.News.Add(gameId!, values.name, values.content);

        setNews((current) => {
            return [
                ...current,
                response
            ]
        });
    }

    const editNews = async (id: string, item: { name: string, content: string}) => {
        try {
            await Api.Game.News.Update(id, gameId!, item)
            AddAlert({
                message: "News updated",
                severity: "success"
            });

        } catch (e: any) {
            AddAlert({
                message: "Failed to update news.",
                severity: "error"
            });
        }
    }

    const deleteNews = async (id: string) => {
        try {
            await Api.Game.News.Delete(id, gameId!)
            AddAlert({
                message: "Config Removed",
                severity: "success"
            });
            setNews((current) => {
                return [...current.filter(c => c.id != id)]
            })

        } catch (e: any) {
            AddAlert({
                message: "Failed to remove news item.",
                severity: "error"
            });
        }
    }

    
    useEffect(() => {
        list("", undefined);
    }, [])
    
    if(loading) return <>Loading</>
    
    const url = `${publicApi}getgamenews/${gameId}`;

    return <>
        <ContentContainer>
            <Typography fontSize={token.font.size.small}>
                News URL :
                <CopyBox content={url} />
            </Typography>
        </ContentContainer>

        <ContentContainer>
            <Section header="New news entry">
                <NewItem onSubmit={addConfig} />
            </Section>
        </ContentContainer>

        <ContentContainer>
            <Stack spacing={1}>
                {news.length === 0 ? <>There are no news to show</> : <>
                    {news.map((c) => {
                        return <GameNewsRow news={c} onChange={(e) => editNews(c.id, e)} onDelete={() => deleteNews(c.id)} key={c.id} />
                    })}
                </>}
            </Stack>
        </ContentContainer>
    </>
}

export default GameNewsScreen;